import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, maxBy, minBy,
  PHONE_CHART_HEIGHT, PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS, shortDay,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { EMPTY, formatNumber } from '../../utils/format';

const OfficeWeekdays = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(90);
  const { data, loading, error, reload } = useInsightData(() => insightsService.officeDaysByWeekday(days), [days]);
  const rows = data || [];

  const total = rows.reduce((s, r) => s + r.officeEntries, 0);
  const busiest = maxBy(rows, 'pct');
  const quietest = minBy(rows, 'pct');
  // A ratio needs a quietest day with someone in, and reads as a finding only when the days differ
  const ratio = busiest && quietest && quietest.pct > 0 ? (busiest.pct / quietest.pct).toFixed(1) : null;
  const showRatio = ratio && ratio !== '1.0';

  const copy = total > 0
    ? [
      `Across our hybrid-work data, ${busiest.day} is the most popular office day.`,
      showRatio && `Teams are in ${ratio}× more often than on ${quietest.day}.`,
      busiest.day !== 'Monday' && `"${busiest.day} is the new Monday."`,
      '📈',
    ].filter(Boolean).join(' ')
    : '';

  return (
    <InsightPage
      title="Office days by weekday"
      description="Which weekday do people actually come into the office?"
      controls={<DateWindowSelect value={days} onChange={setDays} />}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={total === 0}
      emptyMessage="No office days in this window"
    >
      <StatRow>
        <StatCard label="Busiest day" value={busiest?.day || EMPTY} sub={busiest ? `${busiest.pct}% of office days` : ''} variant="primary" />
        <StatCard label="Quietest day" value={quietest?.day || EMPTY} sub={quietest ? `${quietest.pct}% of office days` : ''} />
        <StatCard label="Busiest against quietest" value={ratio ? `${ratio}×` : EMPTY} sub="as many office days" />
        <StatCard label="Office days counted" value={formatNumber(total)} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Share of office attendance by weekday</h5></Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? PHONE_CHART_HEIGHT : 460}>
            <BarChart data={rows} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              <XAxis dataKey="day" {...t.xAxis} {...(isMobile ? { ...PHONE_X_AXIS, tickFormatter: shortDay } : {})} />
              <YAxis unit="%" {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              <Tooltip {...t.tooltip} formatter={(v) => `${v}%`} />
              <Bar dataKey="pct" name="Share of office days">
                {rows.map((r) => (
                  <Cell key={r.day} fill={r === busiest ? t.colors.emphasis : t.colors.neutral} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default OfficeWeekdays;
